import Phaser from 'phaser';
import { gameState } from '@/state/store';
import {
  markerTutorialSteg,
  skipTutorial,
  tutorialFullfort,
  type TutorialSteg,
} from '@/state/tutorial';
import { CoachmarkManager } from './CoachmarkManager';
import { IntroModal } from './IntroModal';

export type TutorialMaalGetter = () => { x: number; y: number; w: number; h: number } | null;

export type TutorialAnker = {
  innboksFab: TutorialMaalGetter;
  innboksFørsteKort: TutorialMaalGetter;
  produksjonsBoks: TutorialMaalGetter;
  redigeringKnapp: TutorialMaalGetter;
  forfatterFab: TutorialMaalGetter;
  nesteDagKnapp: TutorialMaalGetter;
  lukkInnboks?: () => void;
  lukkProduksjon?: () => void;
};

type CoachStegId =
  | 'innboks-fab'
  | 'forste-kort'
  | 'i-produksjon'
  | 'send-i-redigering'
  | 'forfatter-fab'
  | 'neste-dag';

type CoachSteg = {
  id: CoachStegId;
  getMaal: TutorialMaalGetter;
  tekst: string;
  pilRetning: 'opp' | 'ned' | 'venstre' | 'hoyre';
  preferertSide?: 'over' | 'under';
  ferdigFlagg?: TutorialSteg;
  retryMs?: number;
};

const VENTE_MAKS_MS = 10000;
const RETRY_MS_DEFAULT = 300;

export class TutorialController {
  private intro?: IntroModal;
  private coach?: CoachmarkManager;
  private anker?: TutorialAnker;
  private aktivStegIndeks = -1;
  private steg: CoachSteg[] = [];
  private retryTimer?: Phaser.Time.TimerEvent;
  private venteStart = 0;
  private aktiv = false;

  constructor(private scene: Phaser.Scene) {}

  start(anker: TutorialAnker): void {
    if (tutorialFullfort(gameState)) return;
    if (this.aktiv) return;
    this.aktiv = true;
    this.anker = anker;
    this.steg = this.byggSteg(anker);

    this.intro = new IntroModal(this.scene);
    this.coach = new CoachmarkManager(this.scene);

    this.intro.open(
      () => {
        markerTutorialSteg(gameState, 'intro_vist');
        this.startCoachmarks();
      },
      () => this.hoppOver(),
    );
  }

  /**
   * Tving frem en overgang fra et passivt coachmark-steg (uten ferdigFlagg)
   * — f.eks. når brukeren faktisk klikket «Send i redigering» og fase-status
   * gikk fra kontrahert til redigeres.
   */
  advancePassivt(stegId: string): void {
    if (!this.aktiv) return;
    if (this.aktivStegIndeks < 0) return;
    const aktivt = this.steg[this.aktivStegIndeks];
    if (aktivt?.id === stegId) {
      this.nesteCoach();
    }
  }

  meldHendelse(steg: TutorialSteg): void {
    if (!this.aktiv) return;
    markerTutorialSteg(gameState, steg);
    if (this.aktivStegIndeks < 0) return;
    const aktivt = this.steg[this.aktivStegIndeks];
    if (aktivt?.ferdigFlagg === steg) {
      if (steg === 'kontrakt_klikket') this.anker?.lukkInnboks?.();
      if (steg === 'forfatter_vist') this.anker?.lukkProduksjon?.();
      this.nesteCoach();
    }
  }

  destroy(): void {
    this.retryTimer?.remove(false);
    this.retryTimer = undefined;
    this.intro?.close();
    this.coach?.skjul();
    this.intro = undefined;
    this.coach = undefined;
    this.aktiv = false;
  }

  get erAktiv(): boolean {
    return this.aktiv;
  }

  private hoppOver(): void {
    skipTutorial(gameState);
    this.destroy();
  }

  private byggSteg(anker: TutorialAnker): CoachSteg[] {
    return [
      {
        id: 'innboks-fab',
        getMaal: anker.innboksFab,
        tekst: 'Her er innboksen. Klikk for å se dagens manus.',
        pilRetning: 'ned',
        preferertSide: 'over',
        ferdigFlagg: 'innboks_vist',
      },
      {
        id: 'forste-kort',
        getMaal: anker.innboksFørsteKort,
        tekst: 'Trykk «Tilby kontrakt» for å publisere en bok.',
        pilRetning: 'hoyre',
        preferertSide: 'under',
        ferdigFlagg: 'kontrakt_klikket',
      },
      {
        id: 'i-produksjon',
        getMaal: anker.produksjonsBoks,
        tekst: 'Boken din står her nå. Klikk for å se den.',
        pilRetning: 'hoyre',
        preferertSide: 'over',
        ferdigFlagg: 'redigering_klikket',
      },
      {
        id: 'send-i-redigering',
        getMaal: anker.redigeringKnapp,
        tekst: 'Send boken i redigering. Det koster, men gjør den bedre.',
        pilRetning: 'ned',
        preferertSide: 'over',
      },
      {
        id: 'forfatter-fab',
        getMaal: anker.forfatterFab,
        tekst: 'Klikk her for å se forfatteren. Du kan sende henne på kurs for å løfte stats.',
        pilRetning: 'ned',
        preferertSide: 'over',
        ferdigFlagg: 'forfatter_vist',
      },
      {
        id: 'neste-dag',
        getMaal: anker.nesteDagKnapp,
        tekst:
          'Klikk Neste dag for å la tiden gå. Bøker rykker fremover, anmeldelser kommer, salg tikker inn.',
        pilRetning: 'hoyre',
        preferertSide: 'over',
        ferdigFlagg: 'neste_dag_klikket',
      },
    ];
  }

  private startCoachmarks(): void {
    this.intro = undefined;
    this.aktivStegIndeks = 0;
    this.visAktivt();
  }

  private nesteCoach(): void {
    this.coach?.skjul();
    this.retryTimer?.remove(false);
    this.retryTimer = undefined;
    this.aktivStegIndeks += 1;
    if (this.aktivStegIndeks >= this.steg.length) {
      this.fullfor();
      return;
    }
    this.visAktivt();
  }

  private visAktivt(): void {
    const steg = this.steg[this.aktivStegIndeks];
    if (!steg) return;
    this.venteStart = this.scene.time.now;
    this.proverVise(steg);
  }

  private proverVise(steg: CoachSteg): void {
    const maal = steg.getMaal();
    if (maal) {
      this.coach?.vis({
        targetRect: maal,
        tekst: steg.tekst,
        pilRetning: steg.pilRetning,
        preferertSide: steg.preferertSide,
        onHoppOver: () => this.hoppOver(),
      });
      return;
    }

    const alder = this.scene.time.now - this.venteStart;
    if (alder > VENTE_MAKS_MS) {
      this.nesteCoach();
      return;
    }
    this.retryTimer = this.scene.time.delayedCall(steg.retryMs ?? RETRY_MS_DEFAULT, () =>
      this.proverVise(steg),
    );
  }

  private fullfor(): void {
    markerTutorialSteg(gameState, 'fullfort');
    this.coach?.skjul();
    this.aktiv = false;
    const toastEvent = 'tutorial-fullfort';
    this.scene.events.emit(toastEvent);
  }
}
